import React from 'react';
import { IconSearch } from '@tabler/icons-react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import type {
  TracePresentation,
  TracePresentationEvent,
} from '../../trace/presentation';
import { TimelineEventRow } from './timeline-event-row';

export function TimelinePanel({
  presentation,
  unfilteredPresentation,
  selectedEventID,
  eventKinds,
  warningsOnly,
  search,
  loading,
  onToggleKind,
  onWarningsOnly,
  onSearch,
  onSelect,
}: {
  presentation: TracePresentation | null;
  unfilteredPresentation: TracePresentation | null;
  selectedEventID: string;
  eventKinds: Set<string>;
  warningsOnly: boolean;
  search: string;
  loading: boolean;
  onToggleKind: (kind: string) => void;
  onWarningsOnly: (value: boolean) => void;
  onSearch: (value: string) => void;
  onSelect: (event: TracePresentationEvent) => void;
}) {
  const chips = [
    ['user_message', 'User'],
    ['assistant_message', 'Assistant'],
    ['tool_call', 'Tool'],
    ['persist_event', 'Persist'],
    ['llm_call', 'LLM'],
    ['system_event', 'System'],
    ['turn_marker', 'Turn'],
  ];
  const hasEvents = Boolean(
    presentation?.requestGroups.some(group =>
      group.turns.some(
        turn => turn.events.length > 0 || turn.collapsedEvents.length > 0
      )
    )
  );
  const collapsedCountByTurn = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const group of unfilteredPresentation?.requestGroups || []) {
      for (const turn of group.turns) {
        counts.set(
          `${group.requestId}:${turn.turnId}`,
          turn.collapsedEvents.length
        );
      }
    }
    return counts;
  }, [unfilteredPresentation]);
  return (
    <Card className="min-h-0 xl:flex xl:flex-col">
      <CardHeader className="space-y-3 xl:shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Timeline</CardTitle>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={warningsOnly}
              onChange={event => onWarningsOnly(event.currentTarget.checked)}
            />
            Warnings only
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {chips.map(([kind, label]) => (
            <Button
              key={kind}
              type="button"
              variant={eventKinds.has(kind) ? 'default' : 'outline'}
              size="sm"
              onClick={() => onToggleKind(kind)}
            >
              {label}
            </Button>
          ))}
        </div>
        <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <IconSearch className="size-4 text-muted-foreground" />
          <input
            className="w-full bg-transparent outline-none"
            value={search}
            placeholder="Search timeline"
            onChange={event => onSearch(event.currentTarget.value)}
          />
        </label>
      </CardHeader>
      <CardContent className="space-y-2 xl:min-h-0 xl:flex-1 xl:overflow-auto">
        {loading ? (
          <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
            Loading trace...
          </div>
        ) : !hasEvents ? (
          <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
            No timeline events.
          </div>
        ) : (
          presentation?.requestGroups.map(group => (
            <div
              key={group.requestId}
              className="space-y-2 rounded-md border p-2"
            >
              <div className="flex items-center justify-between gap-2 px-1 text-xs font-medium text-muted-foreground">
                <span>{group.label}</span>
                {group.warningCount > 0 ? (
                  <Badge variant="outline">{group.warningCount} warnings</Badge>
                ) : null}
              </div>
              {group.turns.map((turn, turnIndex) => {
                const collapsedCount =
                  collapsedCountByTurn.get(
                    `${group.requestId}:${turn.turnId}`
                  ) || 0;
                return (
                  <div
                    key={`${group.requestId}:${turn.turnId}:${turnIndex}`}
                    className="space-y-2"
                  >
                    <div className="px-1 text-xs text-muted-foreground">
                      Turn {turn.label}
                    </div>
                    {turn.events.map((item, eventIndex) => (
                      <TimelineEventRow
                        key={`${item.event.id}:${eventIndex}`}
                        item={item}
                        selectedEventID={selectedEventID}
                        onSelect={onSelect}
                      />
                    ))}
                    {turn.collapsedEvents.map((item, eventIndex) => (
                      <TimelineEventRow
                        key={`${item.event.id}:collapsed:${eventIndex}`}
                        item={item}
                        selectedEventID={selectedEventID}
                        onSelect={onSelect}
                      />
                    ))}
                    {collapsedCount > 0 && turn.collapsedEvents.length === 0 ? (
                      <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                        {collapsedCount} auxiliary events collapsed
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
